import React from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { getPosWorkspacesDocument } from "app/services/api/accessSystemPosWorkspaces";
import { generateToken } from "app/services/api/management";
import { listCompressorDevicesFromPosWorkspaceDoc } from "app/pages/customers/dsok/utils/listCompressorDevicesFromPosWorkspaces";

const EMPTY = "";

/**
 * Selector for the concreteStation "start compressor after order" target device.
 * Lists `compressor` devices from the customer's POS workspaces (dsokPosWorkspaces) and stores
 * the chosen `settings.concreteStation.compressorWorkspaceId` + `compressorDeviceId`.
 */
export default function CompressorDeviceSelector({
  customerId,
  values,
  setFieldValue,
  t,
}) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(false);
      let doc = null;
      try {
        doc = await getPosWorkspacesDocument();
      } catch (_e) {
        doc = null;
      }
      let found = listCompressorDevicesFromPosWorkspaceDoc(doc || {});
      // Admin editing another customer: session doc may be empty → retry with that customer's token.
      if (found.length === 0 && customerId != null && String(customerId).trim() !== "") {
        try {
          const accessToken = await generateToken(String(customerId).trim());
          if (accessToken) {
            const doc2 = await getPosWorkspacesDocument({ accessToken });
            const rows2 = listCompressorDevicesFromPosWorkspaceDoc(doc2 || {});
            if (rows2.length > found.length) found = rows2;
          }
        } catch (_e) {
          /* ignore — session doc may still have been enough */
        }
      }
      if (!cancelled) {
        setRows(found);
        setLoading(false);
      }
    };
    load().catch(() => {
      if (!cancelled) {
        setLoadError(true);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  const cs = values?.settings?.concreteStation || {};
  const currentWs = String(cs.compressorWorkspaceId ?? "").trim();
  const currentDev = String(cs.compressorDeviceId ?? "").trim();
  const currentKey =
    currentWs !== "" && currentDev !== "" ? `${currentWs}::${currentDev}` : EMPTY;

  const hasCurrentInRows = rows.some((r) => r.selectionKey === currentKey);

  const handleChange = (e) => {
    const key = String(e.target.value || "");
    if (key === EMPTY) {
      setFieldValue("settings.concreteStation.compressorWorkspaceId", "");
      setFieldValue("settings.concreteStation.compressorDeviceId", "");
      return;
    }
    const sep = key.indexOf("::");
    const ws = sep >= 0 ? key.slice(0, sep) : "";
    const dev = sep >= 0 ? key.slice(sep + 2) : "";
    setFieldValue("settings.concreteStation.compressorWorkspaceId", ws);
    setFieldValue("settings.concreteStation.compressorDeviceId", dev);
  };

  const labelText = t(
    "pages.dsokDetails.appSettings.tabs.concreteStation.compressorDevice",
    { defaultValue: "Kompresor (zariadenie)" },
  );

  return (
    <Box sx={{ maxWidth: 520 }}>
      <FormControl fullWidth size="small" disabled={loading}>
        <InputLabel id="concrete-compressor-device-label">
          {labelText}
        </InputLabel>
        <Select
          labelId="concrete-compressor-device-label"
          id="concrete-compressor-device"
          label={labelText}
          value={currentKey}
          onChange={handleChange}
          renderValue={(val) => {
            if (loading) return "…";
            if (!val) return "";
            const row = rows.find((r) => r.selectionKey === val);
            if (row) return `${row.workspaceName} — ${row.deviceName}`;
            return t(
              "pages.dsokDetails.appSettings.tabs.concreteStation.compressorDeviceUnknown",
              { defaultValue: "Vybraný kompresor (mimo aktuálneho zoznamu)" },
            );
          }}
        >
          <MenuItem value={EMPTY}>
            <em>
              {t(
                "pages.dsokDetails.appSettings.tabs.concreteStation.compressorDeviceNone",
                { defaultValue: "— žiadny —" },
              )}
            </em>
          </MenuItem>
          {!hasCurrentInRows && currentKey !== EMPTY && (
            <MenuItem value={currentKey}>
              {t(
                "pages.dsokDetails.appSettings.tabs.concreteStation.compressorDeviceUnknown",
                { defaultValue: "Vybraný kompresor (mimo aktuálneho zoznamu)" },
              )}
            </MenuItem>
          )}
          {rows.map((r) => (
            <MenuItem key={r.selectionKey} value={r.selectionKey}>
              {r.workspaceName} — {r.deviceName}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          {loading ? (
            <>
              <CircularProgress size={12} sx={{ mr: 1 }} />
              {t("pages.dsokDetails.appSettings.tabs.concreteStation.compressorDeviceLoading", {
                defaultValue: "Načítavam kompresory z pracovísk…",
              })}
            </>
          ) : loadError ? (
            t("pages.dsokDetails.appSettings.tabs.concreteStation.compressorDeviceError", {
              defaultValue: "Zoznam kompresorov sa nepodarilo načítať.",
            })
          ) : rows.length === 0 ? (
            t("pages.dsokDetails.appSettings.tabs.concreteStation.compressorDeviceEmpty", {
              defaultValue:
                "V pracoviskách nie je nakonfigurovaný žiadny kompresor. Najprv pridajte zariadenie typu Kompresor - betonáreň.",
            })
          ) : (
            t("pages.dsokDetails.appSettings.tabs.concreteStation.compressorDeviceHelp", {
              defaultValue:
                "Ktorý kompresor sa spustí po úspešnej objednávke. Zoznam pochádza z pracovísk (zariadenia typu Kompresor - betonáreň).",
            })
          )}
        </FormHelperText>
      </FormControl>
    </Box>
  );
}
